import { AppHealthSummaryResponse } from '../../../api/models/app-health-summary-response';
import { IncidentStatusResponse } from '../../../api/models/incident-status-response';

/**
 * App health ("performance") fixtures.
 *
 * Each connected app has its own health profile so switching apps on the App Health page tells a
 * different story: Acme Shopping is recovering from a checkout crash and watching a slow payment
 * gateway, Loyalty is rock solid, Delivery Partner is growing fast and starting to strain, and the
 * Rewards beta is unhealthy because its sync is broken. "All Apps" rolls up the three active apps.
 */

const hoursAgoIso = (hours: number) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
const period = 'LAST_30_DAYS';

const ms = (value: number) => `${value}ms`;

// ------------------------------------------------------------------ summaries

const SHOPPING: AppHealthSummaryResponse = {
  lastUpdated: hoursAgoIso(0.3),
  crashes: { count: 46, status: 'GOOD', trend: 'DOWN', changeFromYesterday: '-6%' },
  responseTime: { averageMs: 312, targetMs: 500, status: 'GOOD', displayValue: ms(312) },
  unresponsivePages: { count: 3, flagged: false, criticalIssues: false, status: 'GOOD' },
  apiResponseTime: { averageMs: 486, status: 'WARNING', displayValue: ms(486) },
  serverUptime: { percentage: 99.82, period, status: 'GOOD', displayValue: '99.82%' },
  incidents: { total: 5, major: 1, minor: 4, period, displayValue: '1 major, 4 minor' },
  networkLatency: { averageMs: 88, status: 'GOOD', displayValue: ms(88) },
  bandwidth: { consumptionGB: 61.4, period, displayValue: '61.4 GB' },
};

const LOYALTY: AppHealthSummaryResponse = {
  lastUpdated: hoursAgoIso(1.1),
  crashes: { count: 6, status: 'EXCELLENT', trend: 'FLAT', changeFromYesterday: '-2%' },
  responseTime: { averageMs: 198, targetMs: 500, status: 'EXCELLENT', displayValue: ms(198) },
  unresponsivePages: { count: 1, flagged: false, criticalIssues: false, status: 'GOOD' },
  apiResponseTime: { averageMs: 142, status: 'EXCELLENT', displayValue: ms(142) },
  serverUptime: { percentage: 99.98, period, status: 'EXCELLENT', displayValue: '99.98%' },
  incidents: { total: 2, major: 0, minor: 2, period, displayValue: '2 minor' },
  networkLatency: { averageMs: 71, status: 'EXCELLENT', displayValue: ms(71) },
  bandwidth: { consumptionGB: 18.3, period, displayValue: '18.3 GB' },
};

const DELIVERY: AppHealthSummaryResponse = {
  lastUpdated: hoursAgoIso(0.6),
  crashes: { count: 19, status: 'WARNING', trend: 'UP', changeFromYesterday: '+21%' },
  responseTime: { averageMs: 455, targetMs: 500, status: 'WARNING', displayValue: ms(455) },
  unresponsivePages: { count: 7, flagged: true, criticalIssues: false, status: 'WARNING' },
  apiResponseTime: { averageMs: 268, status: 'GOOD', displayValue: ms(268) },
  serverUptime: { percentage: 99.91, period, status: 'GOOD', displayValue: '99.91%' },
  incidents: { total: 6, major: 0, minor: 6, period, displayValue: '6 minor' },
  networkLatency: { averageMs: 134, status: 'WARNING', displayValue: ms(134) },
  bandwidth: { consumptionGB: 34.9, period, displayValue: '34.9 GB' },
};

const REWARDS_BETA: AppHealthSummaryResponse = {
  lastUpdated: hoursAgoIso(9 * 24),
  crashes: { count: 31, status: 'CRITICAL', trend: 'UP', changeFromYesterday: '+38%' },
  responseTime: { averageMs: 640, targetMs: 500, status: 'CRITICAL', displayValue: ms(640) },
  unresponsivePages: { count: 12, flagged: true, criticalIssues: true, status: 'CRITICAL' },
  apiResponseTime: { averageMs: 710, status: 'CRITICAL', displayValue: ms(710) },
  serverUptime: { percentage: 97.4, period, status: 'WARNING', displayValue: '97.4%' },
  incidents: { total: 4, major: 1, minor: 3, period, displayValue: '1 major, 3 minor' },
  networkLatency: { averageMs: 188, status: 'WARNING', displayValue: ms(188) },
  bandwidth: { consumptionGB: 1.2, period, displayValue: '1.2 GB' },
};

/** Roll-up of the three active apps (the inactive beta is excluded). */
const ALL_APPS: AppHealthSummaryResponse = {
  lastUpdated: hoursAgoIso(0.3),
  crashes: { count: 71, status: 'GOOD', trend: 'DOWN', changeFromYesterday: '-11%' },
  responseTime: { averageMs: 326, targetMs: 500, status: 'GOOD', displayValue: ms(326) },
  unresponsivePages: { count: 11, flagged: true, criticalIssues: false, status: 'WARNING' },
  apiResponseTime: { averageMs: 340, status: 'GOOD', displayValue: ms(340) },
  serverUptime: { percentage: 99.88, period, status: 'GOOD', displayValue: '99.88%' },
  incidents: { total: 13, major: 1, minor: 12, period, displayValue: '1 major, 12 minor' },
  networkLatency: { averageMs: 97, status: 'GOOD', displayValue: ms(97) },
  bandwidth: { consumptionGB: 114.6, period, displayValue: '114.6 GB' },
};

/** Healthy baseline for apps created during the demo. */
const NEW_APP: AppHealthSummaryResponse = {
  lastUpdated: hoursAgoIso(0.2),
  crashes: { count: 2, status: 'EXCELLENT', trend: 'FLAT', changeFromYesterday: '0%' },
  responseTime: { averageMs: 221, targetMs: 500, status: 'EXCELLENT', displayValue: ms(221) },
  unresponsivePages: { count: 0, flagged: false, criticalIssues: false, status: 'EXCELLENT' },
  apiResponseTime: { averageMs: 164, status: 'EXCELLENT', displayValue: ms(164) },
  serverUptime: { percentage: 99.97, period, status: 'EXCELLENT', displayValue: '99.97%' },
  incidents: { total: 0, major: 0, minor: 0, period, displayValue: 'No incidents' },
  networkLatency: { averageMs: 79, status: 'EXCELLENT', displayValue: ms(79) },
  bandwidth: { consumptionGB: 2.1, period, displayValue: '2.1 GB' },
};

const SUMMARY_BY_APP: Record<number, AppHealthSummaryResponse> = {
  8001: SHOPPING,
  8002: LOYALTY,
  8003: DELIVERY,
  8004: REWARDS_BETA,
};

export function buildHealthSummary(appId?: number | string | null): AppHealthSummaryResponse {
  if (appId == null || appId === '' || appId === 'all') {
    return structuredClone(ALL_APPS);
  }
  return structuredClone(SUMMARY_BY_APP[Number(appId)] ?? NEW_APP);
}

/** Org-wide incident breakdown; totals match the per-app incident counts on the summary above. */
export const DEMO_INCIDENT_STATUS: IncidentStatusResponse = {
  open: 3,
  closed: 10,
  total: 13,
  period,
  lastUpdated: hoursAgoIso(0.3),
  distribution: {
    RESOLVED: 10,
    INVESTIGATING: 2,
    MONITORING: 1,
  },
};

// ------------------------------------------------------------------ detail pages

export interface DemoHealthDetail {
  rootCause: { primary: string; contributing: string[]; technicalDetails: string };
  recommendations: {
    business: { immediate: string[]; shortTerm: string[]; longTerm: string[] };
    developer: { immediate: string[]; shortTerm: string[]; longTerm: string[] };
  };
  timeline: Array<{
    date: string;
    time: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    title: string;
    description: string;
    affectedUsers?: number;
  }>;
  logs: Array<{ timestamp: string; level: 'error' | 'warning' | 'info'; message: string; details?: string }>;
}

const dateLabel = (daysAgo: number) =>
  new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const stamp = (daysAgo: number, time: string) => {
  const d = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
  return `${d.toISOString().slice(0, 10)} ${time}`;
};

const CRASH_SHOPPING: DemoHealthDetail = {
  rootCause: {
    primary:
      'v4.12.0 introduced a null payment-sheet reference on Android 11/12 that crashed checkout; hotfix 4.12.1 shipped 9 days ago and crash volume is back to baseline.',
    contributing: [
      'The new one-tap payment sheet was not covered by the Android 11/12 device matrix in pre-release testing',
      'Staged rollout was advanced from 10% to 100% before the crash-free threshold gate had enough samples',
      'Crashlytics alerting was set to a daily digest, so the spike was noticed through support tickets first',
    ],
    technicalDetails:
      'NullPointerException in PaymentSheetFragment.onCreateView (com.acmemobile.shopping.checkout). Affected about 5% of Android sessions at peak (2.9% of all sessions) versus a 0.6% baseline. iOS was unaffected.',
  },
  recommendations: {
    business: {
      immediate: [
        'Reply to the 1- and 2-star checkout reviews from the last two weeks, acknowledging the fix in 4.12.1',
        'Offer affected customers a small credit for the failed-checkout window',
      ],
      shortTerm: [
        'Add a crash-free-session gate (99.5%) to the release checklist before any rollout passes 25%',
        'Track the checkout conversion dip in the weekly report so the revenue impact is visible to leadership',
      ],
      longTerm: ['Fund an Android device-farm run for every release that touches checkout or payments'],
    },
    developer: {
      immediate: [
        'Keep 4.12.1 pinned as the minimum supported version for Android 11/12 until adoption passes 90%',
        'Add a regression test around PaymentSheetFragment lifecycle restoration',
      ],
      shortTerm: [
        'Switch Crashlytics velocity alerts to real-time for checkout-tagged crashes',
        'Automate the staged-rollout halt when crash-free sessions drop below the gate',
      ],
      longTerm: ['Move the payment sheet behind a remote feature flag for kill-switch rollback without a store release'],
    },
  },
  timeline: [
    {
      date: dateLabel(14),
      time: '09:10',
      severity: 'medium',
      title: 'v4.12.0 released to Google Play',
      description: 'Staged rollout began at 10% and was advanced to 100% the same afternoon.',
    },
    {
      date: dateLabel(12),
      time: '07:45',
      severity: 'critical',
      title: 'Checkout crash rate peaks at 2.9%',
      description: 'Crash-free sessions dipped to 97.1% as the crash reached Android 11/12 devices at full rollout.',
      affectedUsers: 4180,
    },
    {
      date: dateLabel(11),
      time: '10:30',
      severity: 'high',
      title: 'Root cause identified',
      description: 'Engineering traced the crash to a null payment-sheet reference and began a fix.',
    },
    {
      date: dateLabel(9),
      time: '16:20',
      severity: 'medium',
      title: 'Hotfix 4.12.1 released',
      description: 'Fix published with expedited review. Crash rate began falling within hours of adoption.',
    },
    {
      date: dateLabel(6),
      time: '08:00',
      severity: 'low',
      title: 'Crash rate back to baseline',
      description: 'Crash-free sessions recovered to 99.4% and have stayed above 99% since.',
    },
  ],
  logs: [
    { timestamp: stamp(12, '07:41:12'), level: 'error', message: 'NullPointerException: paymentSheet is null', details: 'at com.acmemobile.shopping.checkout.PaymentSheetFragment.onCreateView(PaymentSheetFragment.kt:88)' },
    { timestamp: stamp(12, '07:41:48'), level: 'error', message: 'Fatal exception on thread main (Android 12, Pixel 6)', details: 'Affected versions: 4.12.0 (812), about 5% of Android sessions' },
    { timestamp: stamp(12, '09:02:03'), level: 'warning', message: 'Crash-free sessions below 98% threshold', details: 'Alert sent to #mobile-oncall' },
    { timestamp: stamp(9, '16:22:40'), level: 'info', message: 'Release 4.12.1 (813) published to 100% of users' },
    { timestamp: stamp(6, '08:00:05'), level: 'info', message: 'Crash-free sessions recovered above 99% threshold' },
  ],
};

const CRASH_DELIVERY: DemoHealthDetail = {
  rootCause: {
    primary:
      'Crashes are climbing with courier growth, concentrated in background location updates on iPhone 11 and older running iOS 17.',
    contributing: [
      'Courier count is up 24.6% month over month, which raises absolute crash counts even at a steady rate',
      'The live-tracking screen holds the location session open for the whole shift, increasing memory pressure',
      'Map tile caching is unbounded on long shifts',
    ],
    technicalDetails:
      'Jetsam memory terminations in the LiveRouteViewController (peak resident memory 1.4 GB on iPhone 11). Crash-free sessions are 99.1%, down from 99.5% last month.',
  },
  recommendations: {
    business: {
      immediate: ['Flag older iPhones in the courier onboarding guide and recommend a restart at shift start'],
      shortTerm: ['Budget a performance sprint before the holiday peak when courier volume doubles'],
      longTerm: ['Include a device-refresh stipend in the courier program for devices older than four years'],
    },
    developer: {
      immediate: ['Cap the map tile cache and release the tile layer when the app is backgrounded'],
      shortTerm: ['Add a memory-pressure breadcrumb to crash reports for LiveRouteViewController'],
      longTerm: ['Move route rendering to a lighter snapshot-based map view for long shifts'],
    },
  },
  timeline: [
    { date: dateLabel(21), time: '12:15', severity: 'low', title: 'Courier sign-ups surge', description: 'Metro expansion onboarded 410 new couriers in a week.', affectedUsers: 410 },
    { date: dateLabel(8), time: '18:30', severity: 'medium', title: 'Crash rate crosses 1%', description: 'Dinner-rush crashes grew on older iPhones running iOS 17.', affectedUsers: 265 },
    { date: dateLabel(3), time: '19:05', severity: 'high', title: 'Jetsam terminations spike', description: 'Memory terminations peaked during a recent dinner rush.', affectedUsers: 340 },
  ],
  logs: [
    { timestamp: stamp(3, '19:04:33'), level: 'error', message: 'Terminated due to memory pressure (jetsam)', details: 'LiveRouteViewController — resident memory 1.41 GB, iPhone11,8 / iOS 17.5' },
    { timestamp: stamp(3, '19:10:12'), level: 'warning', message: 'Crash-free sessions 98.6% for last hour' },
    { timestamp: stamp(1, '08:30:00'), level: 'info', message: 'Release 2.8.3 in review: caps map tile cache at 120 MB' },
  ],
};

const API_SHOPPING: DemoHealthDetail = {
  rootCause: {
    primary: 'The third-party payment gateway is responding slowly in the EU region, pushing checkout API latency above target.',
    contributing: [
      'Gateway p95 latency is 1.9 s versus 600 ms normally, limited to EU-routed traffic',
      'Autumn Sale traffic is still 14% above the pre-campaign baseline',
      'Order-confirmation requests are retried synchronously, compounding slow responses',
    ],
    technicalDetails:
      'Average API response time is 486 ms against a 250 ms target; POST /checkout/confirm accounts for 71% of the excess. Incident INC-2041 is open with the gateway provider.',
  },
  recommendations: {
    business: {
      immediate: ['Keep the status-page banner for EU customers while the gateway incident is open'],
      shortTerm: ['Review the gateway SLA and credits for this month'],
      longTerm: ['Qualify a secondary payment provider for failover'],
    },
    developer: {
      immediate: ['Enable the EU gateway fallback route for checkout confirmation'],
      shortTerm: ['Make order confirmation asynchronous with a polling status endpoint'],
      longTerm: ['Add circuit breaking and a second gateway behind the payments facade'],
    },
  },
  timeline: [
    { date: dateLabel(2), time: '13:40', severity: 'high', title: 'Gateway latency alert (INC-2041)', description: 'EU payment gateway p95 latency exceeded 1.5 s.', affectedUsers: 2100 },
    { date: dateLabel(2), time: '14:15', severity: 'medium', title: 'Gateway provider acknowledged', description: 'Provider confirmed degraded performance in their EU region.' },
    { date: dateLabel(1), time: '09:00', severity: 'medium', title: 'Status: investigating', description: 'Latency improved to 1.1 s; provider is still investigating.' },
  ],
  logs: [
    { timestamp: stamp(2, '13:39:51'), level: 'warning', message: 'payments.gateway p95 latency 1.92s (threshold 1.0s)', details: 'region=eu-west-1' },
    { timestamp: stamp(2, '13:41:07'), level: 'error', message: 'POST /checkout/confirm timed out after 5000ms', details: 'orderId=ORD-88213, retry 1/2' },
    { timestamp: stamp(1, '09:00:14'), level: 'info', message: 'Incident INC-2041 set to Monitoring' },
  ],
};

const UNRESPONSIVE_DELIVERY: DemoHealthDetail = {
  rootCause: {
    primary: 'The order-assignment screen blocks the main thread while it re-sorts the nearby-orders list on every location update.',
    contributing: [
      'Location updates arrive every second during peak and each one triggers a full re-sort',
      'List diffing runs on the main thread',
      'Older devices take over 4 seconds to recover after a burst of updates',
    ],
    technicalDetails: '7 pages flagged unresponsive, 5 of them the order-assignment list (p95 main-thread block 3.8 s).',
  },
  recommendations: {
    business: {
      immediate: ['Brief the courier support team on the known slowdown during the dinner rush'],
      shortTerm: ['Prioritize the performance fix ahead of the next metro launch'],
      longTerm: ['Set a courier-app performance budget tied to onboarding targets'],
    },
    developer: {
      immediate: ['Throttle location-driven re-sorts to once every 5 seconds'],
      shortTerm: ['Move list diffing off the main thread'],
      longTerm: ['Adopt incremental list updates for assignments'],
    },
  },
  timeline: [
    { date: dateLabel(6), time: '18:45', severity: 'medium', title: 'Unresponsive-page alert', description: 'Order-assignment list blocked the main thread for over 3 seconds.', affectedUsers: 190 },
    { date: dateLabel(4), time: '19:20', severity: 'high', title: 'ANR rate crosses 1%', description: 'ANR-equivalent stalls peaked at 1.1% during a recent dinner rush.', affectedUsers: 320 },
  ],
  logs: [
    { timestamp: stamp(4, '19:19:40'), level: 'warning', message: 'Main thread blocked for 3814ms', details: 'OrderAssignmentViewController.reloadData' },
    { timestamp: stamp(4, '19:22:02'), level: 'warning', message: '7 pages flagged as unresponsive in the last hour' },
  ],
};

const GENERIC: DemoHealthDetail = {
  rootCause: {
    primary: 'This metric is within its expected range. No single root cause is driving it; changes track normal traffic patterns.',
    contributing: [
      'Weekday and weekend traffic cycles account for most of the day-to-day variation',
      'No deployments or infrastructure changes correlate with movement in this metric this period',
    ],
    technicalDetails: 'Measured over the last 30 days from connected Firebase and Google Play sources, with hourly aggregation.',
  },
  recommendations: {
    business: {
      immediate: ['No action required. Keep monitoring this metric in the weekly health report.'],
      shortTerm: ['Share the current health scorecard with stakeholders at the next product review'],
      longTerm: ['Set quarterly targets for this metric alongside revenue and retention goals'],
    },
    developer: {
      immediate: ['Confirm alert thresholds still match the current targets'],
      shortTerm: ['Add a release-over-release comparison to the dashboard for this metric'],
      longTerm: ['Automate threshold tuning from trailing 90-day baselines'],
    },
  },
  timeline: [
    { date: dateLabel(10), time: '11:00', severity: 'low', title: 'Routine threshold review', description: 'Targets reviewed; no changes needed.' },
  ],
  logs: [
    { timestamp: stamp(0, '06:00:00'), level: 'info', message: 'Daily health aggregation completed' },
    { timestamp: stamp(1, '06:00:00'), level: 'info', message: 'Daily health aggregation completed' },
  ],
};

const DETAILS: Record<string, Record<string, DemoHealthDetail>> = {
  crashes: { '8001': CRASH_SHOPPING, '8003': CRASH_DELIVERY, all: CRASH_SHOPPING },
  'api-response': { '8001': API_SHOPPING, all: API_SHOPPING },
  'unresponsive-pages': { '8003': UNRESPONSIVE_DELIVERY, all: UNRESPONSIVE_DELIVERY },
};

export function buildHealthDetail(metricType: string, appId?: number | string | null): DemoHealthDetail {
  const key = appId == null || appId === '' ? 'all' : String(appId);
  // Only apps with a real story get bespoke detail; everything else reads as healthy/routine.
  return structuredClone(DETAILS[metricType]?.[key] ?? GENERIC);
}
